/**
 * The table's arrow keys, bound ONCE at the `<table>` and resolved through the DOM.
 *
 * ── One listener, not one per cell ───────────────────────────────────────────────────────────
 * A day can carry a dozen exercises of five sets across six columns, and every cell already holds
 * its own typing draft. Giving each input its own navigation handler would put a closure over the
 * grid's shape on every one of them. So the handler sits on the table, each cell publishes its
 * address as a `data-` attribute, and the move is a lookup: nothing about navigation reaches
 * `SetCell`'s props, and nothing here re-renders anything.
 *
 * This module owns BOTH sides of that attribute — `setCellAddress` writes it and `moveFocusOnArrow`
 * reads it — so the encoding cannot drift apart across two files.
 *
 * ── Arriving selects the value ───────────────────────────────────────────────────────────────
 * `select()` rather than a caret at one end, because the gesture this serves is filling a column:
 * the trainer runs down the weights typing over each one, and a selection means the first digit
 * they press replaces rather than appends. It is also what makes the next horizontal arrow a move
 * rather than a collapse — `nextSetCell` reads a full selection as standing at both edges, and the
 * reasoning for that is in its own header.
 *
 * The move fires while the cell being left still holds an uncommitted draft; `focus()` on the target
 * blurs the origin, and leaving commits it through the same `onBlur` a Tab goes through. That
 * ordering is deliberate — the store's value is a number and the draft is a string, and there is
 * exactly one place that conversion happens.
 */

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

import { resolveArrowPress, type SetGridShape } from '../model/setTableNavigation';

const ATTRIBUTE = 'data-set-cell';

/** The address a cell publishes to the table's handler. Zero-based, row first. */
export function setCellAddress(row: number, column: number): string {
  return `${String(row)},${String(column)}`;
}

function readAddress(element: HTMLElement): { row: number; column: number } | null {
  const raw = element.getAttribute(ATTRIBUTE);
  if (raw === null) return null;
  const [row, column] = raw.split(',').map(Number);
  if (row === undefined || column === undefined) return null;
  if (!Number.isInteger(row) || !Number.isInteger(column)) return null;
  return { row, column };
}

/**
 * Move focus to the cell the key names, if it names one. A press that is not ours — anything raised
 * outside a cell input, a modified arrow, a caret with somewhere left to go, an edge, a Tab at
 * either end of the grid — is left entirely alone, default behaviour included.
 *
 * It answers the arrows, Enter and Tab. `Escape` never reaches it: `SetCell` stops the key where the
 * draft it abandons lives, which is also what keeps a mistyped weight from closing the whole day.
 */
export function moveFocusOnArrow(
  event: ReactKeyboardEvent<HTMLTableElement>,
  shape: SetGridShape,
): void {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;

  const origin = input.closest(`[${ATTRIBUTE}]`);
  if (!(origin instanceof HTMLElement)) return;
  const from = readAddress(origin);
  if (from === null) return;

  const outcome = resolveArrowPress(
    from,
    {
      key: event.key,
      // Shift is in here with the rest: Shift+Arrow extends a selection, which is text editing.
      hasModifier: event.metaKey || event.ctrlKey || event.altKey || event.shiftKey,
      // And separately, because Shift+Tab is the BACKWARD walk rather than a modified Tab. The
      // model is the one that decides which of the two readings a key gets.
      hasShift: event.shiftKey,
      selectionStart: input.selectionStart,
      selectionEnd: input.selectionEnd,
      valueLength: input.value.length,
    },
    shape,
  );
  if (outcome.kind === 'yield') return;

  if (outcome.kind === 'hold') {
    // The grid ends here, and the key is still ours: swallowing it is what stops the browser
    // collapsing the selection the trainer is about to type over. See the model's header.
    event.preventDefault();
    return;
  }

  const landing = event.currentTarget.querySelector(
    `[${ATTRIBUTE}="${setCellAddress(outcome.to.row, outcome.to.column)}"] input`,
  );
  // Nothing there means the shape and the DOM disagree, and swallowing the key would be a silent
  // dead end. Hand it back instead — the browser's answer is at least visible.
  if (!(landing instanceof HTMLInputElement)) return;

  event.preventDefault();
  landing.focus();
  landing.select();
}
